import { homedir } from "node:os"
import { join } from "node:path"

import type {
  InterventionConfig,
  ModelOverrideConfig,
  OrchestratorConfig,
  PostWaveConfig,
  ProtocolEnforcementConfig,
  SkillHubConfig,
  SkillPrimingConfig,
  SkillPrimingRouter,
  SkillPrimingTrigger,
} from "./types"
import { defaultScoringConfig } from "./scoring-engine"
import { defaultDecisionHandlerConfig } from "./decision-handler"
import { defaultClosedLoopConfig } from "./closed-loop-learning"
import type { ConfigFileSources, ConfigFileResult } from "./config-file"

/**
 * MetaGovernor config schema exposed to users.
 * This is a Zod-free config interface since Zod parsing is optional
 * in the standalone plugin — the user provides JSON, we coerce with defaults.
 */
export interface MetaGovernorPluginConfig {
  /** Master feature flag — must be true to run the orchestrator. */
  enabled?: boolean

  /** Decision handler (PR 6) */
  decision?: {
    maxHistoryPerSession?: number
    forceContinueAfterStops?: number
    /** v0.18.0: custom warning message template. */
    warnMessageTemplate?: string
    /** v0.18.0: custom escalation message template. */
    escalateMessageTemplate?: string
    /** v0.18.0: custom stop message template. */
    stopMessageTemplate?: string
  }

  /** Memory aggregator (PR 2) */
  memory?: {
    agentmemoryTimeoutMs?: number
    /** v0.18.0: alias for agentmemoryTimeoutMs (was the only name projected). */
    timeoutMs?: number
    boulderStateTimeoutMs?: number
    query?: string
  }

  /** Token predictor (PR 4) */
  tokenPredictor?: {
    compactBurnRateThreshold?: number
    compactUsageThreshold?: number
    switchModelUsageThreshold?: number
    delegateConsecutiveHighBurn?: number
  }

  /** Scoring engine (PR 5) */
  scoring?: {
    continueThreshold?: number
    warnThreshold?: number
    escalateThreshold?: number
    stopThreshold?: number
    /** v0.18.0: was silently dropped by loadOrchestratorConfig */
    paralysisThreshold?: number
    /** v0.18.0: was silently dropped by loadOrchestratorConfig */
    defaultEscalationTarget?: "oracle" | "user"
    /**
     * v0.38.4 Option D: Oracle invocation frequency.
     * @deprecated READ-ONLY fallback — do not set. Canonical knob is
     * `oracle.frequency`; this field is read only for back-compat with
     * pre-v0.38.4 configs and is ignored when `oracle.frequency` is set.
     * Users should set `oracle.frequency` in opencode.jsonc, not this field.
     */
    oracleFrequency?: "per-stop" | "final-only" | "off"
    /**
     * v0.60.0 (F5): the project's history as a scoring signal. Reads
     * agentmemory (recurring errors, co-changes, project profile), cached
     * per project.
     *
     * `weight` defaults to 0 = INFORMATIVE ONLY: the signals appear in the
     * judge's digest but do not move the score. Raise it deliberately once
     * you trust them on your project.
     */
    projectSignals?: {
      enabled?: boolean
      minFrequency?: number
      weight?: number
      cacheTtlMs?: number
    }
  }

  /** Closed-loop learning (PR 3) */
  closedLoop?: {
    enabled?: boolean
    minSeverityToLearn?: "leve" | "media" | "grave"
    maxLessonsPerSession?: number
    saveDecisions?: boolean
    saveLessons?: boolean
    /**
     * v0.61.0 (F0): capture the Oracle's LESSON block in the PLUGIN.
     *
     * Default true. The Oracle's verdict already flows through
     * tool.execute.after, so the plugin parses it and writes the lesson
     * itself when the agent did not — subject to the same value gates as
     * every other lesson. Set false to keep the old behavior (rely on the
     * agent calling omo_remember).
     */
    oracleLessonCapture?: boolean
    /** v0.49.1: anti-loop guard for the Phase 4 auto-remember prompt. */
    autoRemember?: {
      enabled?: boolean
      cooldownMs?: number
      dedupe?: boolean
    }
    /**
     * v0.50.0 (D10): conscience memory gate config. Canonical source for
     * high-value memory persistence (escalate|stop only, value-gated).
     * NOTE: typed structurally (not ConscienceConfig) so config.ts
     * typechecks regardless of T1 landing order — shapes must match
     * ClosedLoopConfig.conscience once T1 lands.
     */
    conscience?: {
      enabled?: boolean
      maxMemoriesPerSession?: number
      toolRoute?: "omo_remember"
      requireNovelty?: boolean
    }
    /**
     * v0.59.0: autonomous skill synthesis. The agent writes its own skills
     * from recurring lessons; the quality gate (src/skill-quality.ts) is the
     * safety net. Default enabled=true — the agent is autonomous, the gate
     * keeps it honest. Set enabled:false to disable entirely.
     */
    autonomousSkills?: {
      enabled?: boolean
      minLessons?: number
      minConfidence?: number
      minPromotionConfidence?: number
      writeProjectSkills?: boolean
      writeGlobalSkills?: boolean
      mirrorGlobal?: boolean
    }
  }

  /** Model override for MetaGovernor internal LLM usage. */
  modelOverride?: ModelOverrideConfig

  /**
   * Oracle invocation frequency. Controls when the plugin invokes
   * Oracle for verification — reduces noisy mid-work escalations.
   *
   * - `"final-only"` (default): Oracle is invoked ONLY at the final-gate.
   *   Even stop-level decisions log without injecting an Oracle prompt.
   *   Zero mid-work interruptions.
   *
   * - `"per-stop"`: Oracle is invoked at the final-gate
   *   (`<promise>DONE</promise>`) AND when the scoring engine reaches the
   *   stop band (action === "stop"). warn/escalate decisions log but do
   *   NOT invoke Oracle mid-work. Brake on emergencies, mandatory at done.
   *   (B3: wording copied from enforcement-resources.ts buildOracleRule;
   *   the previous text said "ONLY at the final-gate ... AND when",
   *   which is self-contradictory.)
   *
   * - `"off"`: Oracle is never invoked. The post-wave gate still requires
   *   `oracleVerified` — set it manually via `omo_recall` if you need it.
   */
  oracle?: {
    /** @default "final-only" */
    frequency?: "per-stop" | "final-only" | "off"
  }

  /** Intervention config for visible decision injection. */
  intervention?: {
    /** @default "silent" */
    mode?: "silent" | "message" | "system"
    /** @default true */
    includeDecisionHistory?: boolean
    /** @default 5 */
    maxHistoryMessages?: number
    /** @default "stop" */
    minActionForMessage?: "warn" | "escalate" | "stop"
    /**
     * v0.10.0: rate-limit interventions to break instruction loops.
     * v0.51.x (Wave A P3): severity-tiered quota — only escalate/stop consume.
     * @default 5
     */
    maxInterventionsPerSession?: number
    /**
     * v0.10.0: stop injecting after <promise>DONE</promise> + Oracle verified.
     * @default true
     */
    respectDoneSignal?: boolean
    /**
     * v0.15.0: split per-phase hint from terminal signal. See types.ts.
     * @default false
     */
    phaseAwareDoneSignal?: boolean
    /**
     * v0.19.0: persist intervention messages to the session (TUI-visible).
     * @default true
     */
    persistToSession?: boolean
    /**
     * v0.31.1: overflow compaction loop guard. Defends against opencode
     * bug #27924 (recursive overflow-only compactions). When the guard
     * trips, the plugin flips opencode's autocontinue to disabled so the
     * model can resume its pending tasks.
     *
     * v0.34.2: defaults aligned to ON (was OFF in v0.31.x - v0.34.1).
     * Users upgrading from v0.34.0/0.34.1 should set nabled: false
     * explicitly if they relied on the previous OFF default.
     */
    compactionLoopGuard?: {
      /** @default true */
      enabled?: boolean
      /** @default 1 */
      maxOverflowRecoveries?: number
    }
  }

  /** Sisyphus protocol enforcement config. */
  /**
   * v0.41.0: Tier 1 governance - active policy enforcement via OpenCode hooks.
   *
   * Before v0.41.0 the plugin could only OBSERVE tool calls. This block adds
   * the ability to BLOCK and REWRITE: permission.ask denies dangerous ops,
   * tool.definition rewrites tool descriptions, command.execute.before filters
   * destructive shell commands, and experimental.provider.small_model forces
   * cheap models on subagents.
   *
   * All sub-blocks default to no-op (no behavior change vs v0.40.0) - features
   * require explicit opt-in via the respective flags.
   */
  governance?: {
    /** permission.ask policy. Default "allow" + empty patterns = no-op (preserves v0.40.0). */
    permissionPolicy?: {
      /**
       * @default "allow"
       * - "allow": plugin only denies when a pattern matches (default).
       * - "deny-on-match": same as "allow" - kept for future semantic.
       * - "ask-on-match": only escalate to user prompt on pattern match.
       */
      mode?: "allow" | "deny-on-match" | "ask-on-match"
      /** Bash command regex patterns to deny (matched anywhere in command). */
      bashDenyPatterns?: string[]
      /** Bash command patterns that require user confirmation. */
      bashAskPatterns?: string[]
      /** Edit path glob patterns to deny (matched against file path). */
      editDenyPaths?: string[]
      /** Edit path patterns that require user confirmation. */
      editAskPaths?: string[]
      /** webfetch URL host patterns to deny (matched against host). */
      webfetchDenyHosts?: string[]
    }
    /** tool.definition policy. Default disabled (no behavior change). */
    toolRewrite?: {
      /** @default false */
      enabled?: boolean
      /** Suffix to append to every tool description visible to LLM. */
      descriptionSuffix?: string
      /** Hide specific tools entirely from LLM by clearing description. */
      hideToolIDs?: string[]
      /** Override parameter descriptions per tool. */
      parameterOverrides?: Record<string, Record<string, string>>
    }
    /** command.execute.before policy. Default disabled. */
    commandFilter?: {
      /** @default false */
      enabled?: boolean
      /** Dangerous command regex patterns - throw on match to block execution. */
      denyPatterns?: string[]
      /** Prefix injected as warning text part (does not block, only warns). */
      replacementPrefix?: string
    }
    /** experimental.provider.small_model override for subagent cost control. Default disabled. */
    smallModelOverride?: {
      /** @default false */
      enabled?: boolean
      /** Force this model ID for subagents (e.g. "claude-3-5-haiku-20241022"). */
      modelID?: string
      /** Provider ID (e.g. "anthropic"). */
      providerID?: string
    }
  }

  protocolEnforcement?: {
    enabled?: boolean
    path?: string
    injectIntoSystem?: boolean
    auditToolCalls?: boolean
  }

  /** Wave B workflow gates (explore-before-implement). All default false. */
  workflowGates?: {
    enabled?: boolean
    requirePlan?: boolean
  }

  /** Graph sync config for auto-initializing codegraph/graphify. */
  graphSync?: {
    /** @default true */
    enabled?: boolean
    /** @default false */
    watch?: boolean
    /** @default true */
    autoInstall?: boolean
    /** @default 120000 */
    installTimeoutMs?: number
    /** v0.22.0: when true, graph-sync init sweeps orphaned graphify/codegraph
     * @default true
     * processes left by previous crashed runs. Default true. */
    killOrphanedOnInit?: boolean
    /**
     * v0.25.1: on plugin load, fetch origin and reindex if local HEAD is behind.
     * @default true
     */
    reindexOnFetch?: boolean
    /**
     * v0.25.1: branch to fetch + compare against.
     * @default "main"
     */
    fetchBranch?: string
    /** v0.26.0: auto-upgrade installed codegraph and graphify binaries on graph-sync init.
     * @default true
     * Default true. Tiered probe + pip --upgrade flag fixes 6 silent-failure bugs from v0.24.x. */
    autoUpgrade?: boolean
    /** v0.26.0: filesystem path for the upgrade cache file (tracks latest-known
     * @default "~/.omo-meta-governor/upgrade-cache.json"
     * codegraph/graphify versions to avoid re-fetching the npm/PyPI registry on every load). */
    upgradeCachePath?: string
    /** v0.26.0: when true, run 'graphify check-update' after upgrade and emit a
     * 'graphify-reextract-triggered' diagnostic code if the schema changed.
     * @default true
     * Default true. */
    checkGraphifyNeedsUpdate?: boolean
    /** v0.27.0: when true, register the project graph in the global graphify
     * registry after initial install (so 'graphify global list' surfaces it).
     * @default false
     * Default false (opt-in — multi-project users want explicit control). */
    addToGlobalGraph?: boolean
  }
  /** v0.28.0: CLI-Anything hub auto-install + auto-upgrade. Opt-out
   *  (enabled by default; set `cliAnything.enabled: false` to disable).
   *  When enabled, the plugin ensures `cli-anything-hub` (pip) and
   *  `cli-hub-meta-skill` (npx skills) are installed and current.
   *  Canonical projection is `!== false` (see loadOrchestratorConfig
   *  cliAnything projection and orchestrator.ts CLI-Anything defaults). */
  cliAnything?: {
    /** @default true (opt-out via `cliAnything.enabled: false`;
     * canonical `!== false` projection in orchestrator.ts) */
    enabled?: boolean
    /** @default true */
    autoInstall?: boolean
    /** @default true */
    autoUpgrade?: boolean
    /** @default newPluginPaths().cliAnythingUpgradeCheck
     * (effective default applied at call-site, plugin.ts) */
    cachePath?: string
    /** @default 86400000 (24h; effective default applied at call-site, plugin.ts:649) */
    upgradeCheckTtlMs?: number
    /** @default "cli-hub" */
    cliHubBin?: string
    /** @default "npx skills" */
    skillsBin?: string
    /** @default "global" */
    installScope?: "global" | "project"
  }
  /** Skill priming config (v0.20.0): proactive skill-selection nudge. */
skillPriming?: {
    /** @default false */
enabled?: boolean
    /** @default "firstImplement" */
trigger?: SkillPrimingTrigger
    /** @default "both" */
    router?: SkillPrimingRouter
    /** v0.34.0: enforcement mode for the skill-priming directive. */
    /** @default "block" */
    enforceMode?: "directive" | "block"
}
  /** Skill hub config (v0.32.0): registry-backed catalog + hybrid search. */
  skillHub?: {
    /** @default true */
    enabled?: boolean
    /** @default 3600000 */
    syncIntervalMs?: number
    bootstrapUrl?: string
    searchFallbackUrl?: string
    downloadBaseUrl?: string
    embedBaseUrl?: string
    embedModel?: string
    minInstalls?: number
    filterDuplicates?: boolean
    depsCheck?: boolean
    choreDir?: string
    /**
     * v0.35.0: write fetched hub skills to <cwd>/.agents/skills/<slug>/SKILL.md.
     * @default true
     */
    autoMaterialize?: boolean
  }

  /** Post-wave workflow gate (v0.21.0): landing directives after Oracle-approved waves. */
  postWave?: PostWaveConfig
  /**
   * v0.25.0: explicit routing between codegraph and graphify.
   * "auto" (default) | "codegraph" | "graphify" | "alternate".
   */
  graphRetrieval?: {
    /** @default "auto" */
    preferredTool?: "auto" | "codegraph" | "graphify" | "alternate"
    /** v0.27.0: when true, prefer the locally-installed codegraph binary
     * @default false
     * (node_modules/.bin/codegraph) over the npx-resolved one. Default false. */
    preferLocalCodegraph?: boolean
    /** v0.27.0: route omo_search queries to codegraph `context` instead of
     * `explore`. Default false. context returns a focused code window; explore
     * @default false
     * returns a conceptual explanation. */
    contextRouting?: boolean
  }
}

/**
 * Project the full MetaGovernorPluginConfig into OrchestratorConfig.
 * Missing sub-configs fall back to module defaults.
 */
export function loadOrchestratorConfig(
  pluginConfig: Partial<MetaGovernorPluginConfig> | undefined,
): OrchestratorConfig {
  const full: MetaGovernorPluginConfig = {
    enabled: false,
    ...pluginConfig,
  }

  const baseScoring = defaultScoringConfig()
  const baseDecision = defaultDecisionHandlerConfig()
  const baseClosedLoop = defaultClosedLoopConfig()

  return {
    enabled: full.enabled === true,
    // v0.18.0: support both `timeoutMs` and `agentmemoryTimeoutMs` (alias for back-compat)
    memory: {
      enabled: true,
      query: full.memory?.query ?? "meta_governor_context",
      timeoutMs: full.memory?.timeoutMs ?? full.memory?.agentmemoryTimeoutMs ?? 2000,
    },
    tokenPredictor: {
      compactBurnRateThreshold:
        full.tokenPredictor?.compactBurnRateThreshold ?? 500,
      compactUsageThreshold:
        full.tokenPredictor?.compactUsageThreshold ?? 0.85,
      switchModelUsageThreshold:
        full.tokenPredictor?.switchModelUsageThreshold ?? 0.95,
      delegateConsecutiveHighBurn:
        full.tokenPredictor?.delegateConsecutiveHighBurn ?? 5,
    },
    // v0.18.0: project ALL scoring fields. Previously paralysisThreshold
    // and defaultEscalationTarget were silently dropped.
    scoring: {
      ...baseScoring,
      ...(full.scoring?.continueThreshold !== undefined
        ? { continueThreshold: full.scoring.continueThreshold }
        : {}),
      ...(full.scoring?.warnThreshold !== undefined
        ? { warnThreshold: full.scoring.warnThreshold }
        : {}),
      ...(full.scoring?.escalateThreshold !== undefined
        ? { escalateThreshold: full.scoring.escalateThreshold }
        : {}),
      ...(full.scoring?.stopThreshold !== undefined
        ? { stopThreshold: full.scoring.stopThreshold }
        : {}),
      ...(full.scoring?.paralysisThreshold !== undefined
        ? { paralysisThreshold: full.scoring.paralysisThreshold }
        : {}),
      ...(full.scoring?.defaultEscalationTarget !== undefined
        ? { defaultEscalationTarget: full.scoring.defaultEscalationTarget }
        : {}),
      // v0.60.0 (F5): project-history signals. weight=0 by default →
      // informative only (surfaced in the digest, score untouched).
      projectSignals: {
        enabled: full.scoring?.projectSignals?.enabled ?? true,
        minFrequency: full.scoring?.projectSignals?.minFrequency ?? 3,
        weight: full.scoring?.projectSignals?.weight ?? 0,
        cacheTtlMs: full.scoring?.projectSignals?.cacheTtlMs ?? 300_000,
      },
      // Oracle invocation frequency is DERIVED from `oracle.frequency`
      // (canonical). `scoring.oracleFrequency` is a deprecated read-only
      // fallback for pre-v0.38.4 configs — never a user knob. Fallback order:
      // oracle.frequency -> scoring.oracleFrequency (deprecated) -> "final-only".
      oracleFrequency:
        full.oracle?.frequency ??
        full.scoring?.oracleFrequency ??
        "final-only",
    },
    // v0.18.0: project all closedLoop fields, not just saveDecisions.
    // Previously maxLessonsPerSession, enabled, minSeverityToLearn, and
    // saveLessons were silently dropped.
    closedLoop: {
      ...baseClosedLoop,
      ...(full.closedLoop?.enabled !== undefined
        ? { enabled: full.closedLoop.enabled }
        : {}),
      ...(full.closedLoop?.minSeverityToLearn !== undefined
        ? { minSeverityToLearn: full.closedLoop.minSeverityToLearn }
        : {}),
      ...(full.closedLoop?.maxLessonsPerSession !== undefined
        ? { maxLessonsPerSession: full.closedLoop.maxLessonsPerSession }
        : {}),
      ...(full.closedLoop?.saveDecisions !== undefined
        ? { saveDecisions: full.closedLoop.saveDecisions }
        : {}),
      ...(full.closedLoop?.saveLessons !== undefined
        ? { saveLessons: full.closedLoop.saveLessons }
        : {}),
      // v0.61.0 (F0): the plugin captures the Oracle's LESSON block itself.
      // Default true — the lesson no longer depends on the agent's
      // compliance with the piggyback instruction.
      oracleLessonCapture: full.closedLoop?.oracleLessonCapture !== false,
      // v0.50.0 (D2): autoRemember canonical defaults — OPT-IN.
      // baseClosedLoop still defaults enabled:true (owned by T4); the
      // projection overrides to false so unset config never enables memory
      // writes. Explicit enabled:true is still honored. cooldownMs/dedupe
      // fall back to base (300_000 / true).
      autoRemember: {
        ...baseClosedLoop.autoRemember,
        ...full.closedLoop?.autoRemember,
        enabled: full.closedLoop?.autoRemember?.enabled ?? false,
        cooldownMs:
          full.closedLoop?.autoRemember?.cooldownMs ??
          baseClosedLoop.autoRemember?.cooldownMs ??
          300_000,
        dedupe:
          full.closedLoop?.autoRemember?.dedupe ??
          baseClosedLoop.autoRemember?.dedupe ??
          true,
      },
      // v0.50.0 (D10): conscience passthrough with opt-in defaults.
      // maxMemoriesPerSession has no default — passthrough only.
      conscience: {
        enabled: full.closedLoop?.conscience?.enabled ?? false,
        ...(full.closedLoop?.conscience?.maxMemoriesPerSession !== undefined
          ? { maxMemoriesPerSession: full.closedLoop.conscience.maxMemoriesPerSession }
          : {}),
        toolRoute: full.closedLoop?.conscience?.toolRoute ?? "omo_remember",
        requireNovelty: full.closedLoop?.conscience?.requireNovelty ?? true,
      },
      // v0.59.0: autonomous skill synthesis — enabled by default (the agent
      // is autonomous; the quality gate is the safety net).
      autonomousSkills: {
        enabled: full.closedLoop?.autonomousSkills?.enabled ?? true,
        minLessons: full.closedLoop?.autonomousSkills?.minLessons ?? 3,
        minConfidence: full.closedLoop?.autonomousSkills?.minConfidence ?? 0.6,
        minPromotionConfidence: full.closedLoop?.autonomousSkills?.minPromotionConfidence ?? 0.7,
        writeProjectSkills: full.closedLoop?.autonomousSkills?.writeProjectSkills ?? true,
        writeGlobalSkills: full.closedLoop?.autonomousSkills?.writeGlobalSkills ?? false,
        mirrorGlobal: full.closedLoop?.autonomousSkills?.mirrorGlobal ?? false,
      },
    // Cast: ClosedLoopConfig.conscience lands in parallel T1 — the cast keeps
    // this compiling before/after without drift. Safe to drop once T1's
    // ConscienceConfig is the declared field type (shapes already match).
    } as OrchestratorConfig["closedLoop"],
    // v0.18.0: project all decision fields, including message templates.
    decision: {
      ...baseDecision,
      ...(full.decision?.maxHistoryPerSession !== undefined
        ? { maxHistoryPerSession: full.decision.maxHistoryPerSession }
        : {}),
      ...(full.decision?.forceContinueAfterStops !== undefined
        ? { forceContinueAfterStops: full.decision.forceContinueAfterStops }
        : {}),
      ...(full.decision?.warnMessageTemplate !== undefined
        ? { warnMessageTemplate: full.decision.warnMessageTemplate }
        : {}),
      ...(full.decision?.escalateMessageTemplate !== undefined
        ? { escalateMessageTemplate: full.decision.escalateMessageTemplate }
        : {}),
      ...(full.decision?.stopMessageTemplate !== undefined
        ? { stopMessageTemplate: full.decision.stopMessageTemplate }
        : {}),
    },
    modelOverride: full.modelOverride
      ? {
          providerID: full.modelOverride.providerID,
          modelID: full.modelOverride.modelID,
          modelLimit: full.modelOverride.modelLimit,
          temperature: full.modelOverride.temperature ?? 0.2,
          topP: full.modelOverride.topP ?? 1,
          maxTokens: full.modelOverride.maxTokens ?? 2048,
          reasoning: full.modelOverride.reasoning ?? false,
          verbosity: full.modelOverride.verbosity ?? "minimal",
        }
      : undefined,
    intervention: {
      mode: full.intervention?.mode ?? "silent",
      includeDecisionHistory: full.intervention?.includeDecisionHistory ?? true,
      maxHistoryMessages: full.intervention?.maxHistoryMessages ?? 5,
      // v0.10.0: default is "stop" — see orchestrator.ts for rationale.
      minActionForMessage: full.intervention?.minActionForMessage ?? "stop",
      // v0.10.0: rate-limit interventions to break instruction loops.
      // v0.51.x (Wave A P3): default 5 (was 3); only escalate/stop consume.
      maxInterventionsPerSession:
        full.intervention?.maxInterventionsPerSession ?? 5,
      // v0.10.0: stop injecting after the agent signals <promise>DONE</promise>
      // AND Oracle has verified the work.
      respectDoneSignal: full.intervention?.respectDoneSignal ?? true,
      // v0.15.0: split per-phase hint (DONE) from terminal (PLAN-COMPLETE).
      // Default false (preserves v0.10.0–v0.14.x behavior). Users with
      // multi-phase plans opt in to phase-aware DONE gating.
      phaseAwareDoneSignal: full.intervention?.phaseAwareDoneSignal ?? false,
      // v0.19.0: persist interventions to the session so the user sees
      // them in the TUI. The transform push reaches the model but is never
      // persisted in OpenCode 1.18.x (no write path for synthetic messages).
      persistToSession: full.intervention?.persistToSession ?? true,
      // v0.31.1: overflow loop guard. Defaults to OFF (opt-in) so users
      // upgrading from v0.30.x do not get a silent behavior change. Set
      // enabled:true if you are affected by opencode issue #27924 (recursive
      // overflow-only compactions). Max consecutive overflows tolerated
      // before the guard flips autocontinue to disabled.
      compactionLoopGuard: {
        // v0.34.2: defaults aligned to schema/orchestrator intent. v0.34.1
        // had false/2 in config.ts but true/1 in orchestrator.ts +
        // generate-schema.ts — drift left users without the opencode #27924
        // defense enabled by default.
        enabled:
          full.intervention?.compactionLoopGuard?.enabled ?? true,
        maxOverflowRecoveries: Math.max(
          1,
          full.intervention?.compactionLoopGuard?.maxOverflowRecoveries ?? 1,
        ),
      },
    } as InterventionConfig,

    // v0.21.0: project all postWave fields with defaults. `enabled` derives
    // from phaseAwareDoneSignal when the user hasn't set it explicitly.
    postWave: {
      enabled:
        full.postWave?.enabled ?? (full.intervention?.phaseAwareDoneSignal === true),
      repoMode: full.postWave?.repoMode ?? "auto",
      aasToolPrefix: full.postWave?.aasToolPrefix ?? "aas",
      ciTimeoutMs: full.postWave?.ciTimeoutMs ?? 600_000,
      maxRetriesPerWave: full.postWave?.maxRetriesPerWave ?? 1,
      reinjectCooldownMs: full.postWave?.reinjectCooldownMs ?? 60_000,
      respectSilentMode: full.postWave?.respectSilentMode ?? false,
      ...(full.postWave?.ownRepoDirective !== undefined
        ? { ownRepoDirective: full.postWave.ownRepoDirective }
        : {}),
      ...(full.postWave?.thirdPartyDirective !== undefined
        ? { thirdPartyDirective: full.postWave.thirdPartyDirective }
        : {}),
    } as PostWaveConfig,
    protocolEnforcement: {
      enabled: full.protocolEnforcement?.enabled ?? false,
      path: full.protocolEnforcement?.path,
      injectIntoSystem: full.protocolEnforcement?.injectIntoSystem ?? false,
      auditToolCalls: full.protocolEnforcement?.auditToolCalls ?? false,
    } as ProtocolEnforcementConfig,
    // Wave B: project workflow gates with defaults (all false = no behavior change).
    workflowGates: {
      enabled: full.workflowGates?.enabled ?? false,
      requirePlan: full.workflowGates?.requirePlan ?? false,
    },
    // v0.20.0: project all skillPriming fields with defaults.
    // v0.33.1: enabled defaults to TRUE so the skill workflow fires out of the box;
    // router default changed from 'both' to 'registry' since AAS MCP is retired.
    skillPriming: {
      enabled: full.skillPriming?.enabled ?? true,
      trigger: full.skillPriming?.trigger ?? "firstImplement",
      router: full.skillPriming?.router ?? "registry",
      // v0.34.0: opt-in enforcement. Secure-by-default: 'block' hard-gates implementation tools.
      enforceMode: full.skillPriming?.enforceMode ?? "block",
    },
skillHub: {
      enabled: full.skillHub?.enabled ?? false,
      syncIntervalMs: full.skillHub?.syncIntervalMs ?? 86400000,
      bootstrapUrl: full.skillHub?.bootstrapUrl ?? "https://skills-library.com/api/skills.json",
      searchFallbackUrl: full.skillHub?.searchFallbackUrl ?? "https://skills.sh/api/search",
      downloadBaseUrl: full.skillHub?.downloadBaseUrl ?? "https://skills.sh/api/download",
      embedBaseUrl: full.skillHub?.embedBaseUrl ?? "http://127.0.0.1:3114/v1",
      embedModel: full.skillHub?.embedModel ?? "bge-m3",
      minInstalls: full.skillHub?.minInstalls ?? 0,
      filterDuplicates: full.skillHub?.filterDuplicates !== false,
      depsCheck: full.skillHub?.depsCheck !== false,
      choreDir: full.skillHub?.choreDir ?? join(homedir(), ".agents", "skills"),
      // v0.35.0: default true so hub-fetched skills land on disk and opencode can Read() them.
      autoMaterialize: full.skillHub?.autoMaterialize ?? true,
    } as SkillHubConfig,
    // v0.22.0: project graphSync killOrphanedOnInit with default true.
graphSync: {
      killOrphanedOnInit: full.graphSync?.killOrphanedOnInit ?? true,
      reindexOnFetch: full.graphSync?.reindexOnFetch !== false,
      fetchBranch: full.graphSync?.fetchBranch ?? "main",
      // v0.26.0: auto-upgrade + graphify check-update defaults.
      autoUpgrade: full.graphSync?.autoUpgrade !== false,
      upgradeCachePath: full.graphSync?.upgradeCachePath,
      checkGraphifyNeedsUpdate: full.graphSync?.checkGraphifyNeedsUpdate !== false,
      // v0.27.0: opt-in global graph registration.
      addToGlobalGraph: full.graphSync?.addToGlobalGraph === true,
},
    // v0.25.0: explicit codegraph/graphify routing.
    graphRetrieval: {
      preferredTool: full.graphRetrieval?.preferredTool ?? "auto",
      // v0.27.0: extended routing knobs.
      preferLocalCodegraph: full.graphRetrieval?.preferLocalCodegraph === true,
      contextRouting: full.graphRetrieval?.contextRouting === true,
    },
    // v0.28.0: CLI-Anything hub auto-install + auto-upgrade projection.
    cliAnything: {
      enabled: full.cliAnything?.enabled !== false,
      autoInstall: full.cliAnything?.autoInstall !== false,
      autoUpgrade: full.cliAnything?.autoUpgrade !== false,
      cachePath: full.cliAnything?.cachePath,
      upgradeCheckTtlMs: full.cliAnything?.upgradeCheckTtlMs,
      cliHubBin: full.cliAnything?.cliHubBin ?? "cli-hub",
      skillsBin: full.cliAnything?.skillsBin ?? "npx skills",
      installScope: full.cliAnything?.installScope ?? "global",
    },
    // v0.43.0 Phase 2 part 2: activate governance defaults — toolRewrite + commandFilter enabled by default.
    // permissionPolicy stays opt-in via mode; smallModelOverride stays opt-in via enabled.
    governance: {
      permissionPolicy: full.governance?.permissionPolicy
        ? {
            mode: full.governance.permissionPolicy.mode,
            bashDenyPatterns: full.governance.permissionPolicy.bashDenyPatterns,
            bashAskPatterns: full.governance.permissionPolicy.bashAskPatterns,
            editDenyPaths: full.governance.permissionPolicy.editDenyPaths,
            editAskPaths: full.governance.permissionPolicy.editAskPaths,
            webfetchDenyHosts: full.governance.permissionPolicy.webfetchDenyHosts,
          }
        : undefined,
      toolRewrite: {
        enabled: full.governance?.toolRewrite?.enabled ?? true,
        descriptionSuffix: full.governance?.toolRewrite?.descriptionSuffix,
        hideToolIDs: full.governance?.toolRewrite?.hideToolIDs,
        parameterOverrides: full.governance?.toolRewrite?.parameterOverrides,
      },
      commandFilter: {
        enabled: full.governance?.commandFilter?.enabled ?? true,
        denyPatterns: full.governance?.commandFilter?.denyPatterns,
        replacementPrefix: full.governance?.commandFilter?.replacementPrefix,
      },
      smallModelOverride: full.governance?.smallModelOverride
        ? {
            enabled: full.governance.smallModelOverride.enabled,
            modelID: full.governance.smallModelOverride.modelID,
            providerID: full.governance.smallModelOverride.providerID,
          }
        : undefined,
    },
  }
}

/**
 * Check whether the MetaGovernor is enabled. Returns false if config is undefined.
 */
export function isMetaGovernorEnabled(
  config: Partial<MetaGovernorPluginConfig> | undefined,
): boolean {
  // v0.18.0: support both `enabled` at top level and the wrapped
  // `meta_governor.enabled` shape that OpenCode uses in opencode.jsonc.
  if (!config) return false
  if (config.enabled === true) return true
  const mg = (config as unknown as { meta_governor?: { enabled?: boolean } }).meta_governor
  return mg?.enabled === true
}

/**
 * Load orchestrator config from all available sources: config file (JSONC)
 * with priority: CLI inline > project config > user config > defaults.
 */
export async function loadOrchestratorConfigFromSources(
  sources: ConfigFileSources = {},
): Promise<OrchestratorConfig> {
  const { loadMetaGovernorConfig } = await import("./config-file")
  const result: ConfigFileResult = await loadMetaGovernorConfig(sources)
  return loadOrchestratorConfig(result.config)
}
